import { useEffect, useState } from 'react';
import { AlertCircle, CheckCircle2, AlertTriangle } from 'lucide-react';
import { clsx } from 'clsx';
import type { BudgetWithProgress } from '../../types';
import api from '../../services/api';

interface BudgetHintProps {
  type: 'shared' | 'personal';
  categoryId: string | null;
  subcategoryId?: string | null;
  bankAccountId?: string | null; // Pour les budgets personnels avec ciblage de compte
  month?: number;
  year?: number;
  operationDate?: string; // Date de l'opération (YYYY-MM-DD) pour les budgets hebdomadaires
  formatCurrency: (amount: number) => string;
  currentAmount?: number; // Montant en cours de saisie pour le calcul en temps réel
  excludeOperationId?: string; // ID de l'opération en cours d'édition à exclure du calcul
}

function getStatusIcon(status: BudgetWithProgress['status']) {
  switch (status) {
    case 'on_track':
      return <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />;
    case 'warning':
      return <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />;
    case 'exceeded':
      return <AlertCircle className="w-3.5 h-3.5 text-red-500" />;
  }
}

function getStatusBgColor(status: BudgetWithProgress['status']) {
  switch (status) {
    case 'on_track':
      return 'bg-green-50 border-green-200';
    case 'warning':
      return 'bg-amber-50 border-amber-200';
    case 'exceeded':
      return 'bg-red-50 border-red-200';
  }
}

export default function BudgetHint({
  type,
  categoryId,
  subcategoryId,
  bankAccountId,
  month,
  year,
  operationDate,
  formatCurrency,
  currentAmount = 0,
  excludeOperationId,
}: BudgetHintProps) {
  const [budget, setBudget] = useState<BudgetWithProgress | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!categoryId) {
      setBudget(null);
      return;
    }

    const fetchBudget = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.append('type', type);
        params.append('categoryId', categoryId);
        if (subcategoryId) params.append('subcategoryId', subcategoryId);
        if (bankAccountId) params.append('bankAccountId', bankAccountId);
        if (month) params.append('month', month.toString());
        if (year) params.append('year', year.toString());
        // Envoyer la date d'opération pour les budgets hebdomadaires
        if (operationDate) params.append('operationDate', operationDate);
        // Exclure l'opération en cours d'édition du calcul
        if (excludeOperationId) params.append('excludeOperationId', excludeOperationId);

        const response = await api.get(`/budgets/category-progress?${params.toString()}`);
        if (response.data.success) {
          setBudget(response.data.data);
        } else {
          setBudget(null);
        }
      } catch {
        setBudget(null);
      } finally {
        setLoading(false);
      }
    };

    fetchBudget();
  }, [type, categoryId, subcategoryId, bankAccountId, month, year, operationDate, excludeOperationId]);

  if (loading || !budget) {
    return null;
  }

  // Calculer la progression avec le montant en cours de saisie
  const projectedSpent = budget.spent + currentAmount;
  const projectedRemaining = budget.amount - projectedSpent;
  const projectedPercent = budget.amount > 0 ? (projectedSpent / budget.amount) * 100 : 0;

  // Déterminer le statut projeté
  let projectedStatus: BudgetWithProgress['status'] = 'on_track';
  if (projectedPercent >= 100) {
    projectedStatus = 'exceeded';
  } else if (projectedPercent >= 80) {
    projectedStatus = 'warning';
  }

  return (
    <div
      className={clsx(
        'absolute z-50 bottom-full mb-1.5 left-1/2 -translate-x-1/2 w-max min-w-full max-w-[250px] px-2 py-1.5 rounded border text-xs shadow-sm',
        getStatusBgColor(projectedStatus)
      )}
    >
      <div className="flex items-center justify-center gap-1.5">
        {getStatusIcon(projectedStatus)}
        <span className="text-gray-600">{formatCurrency(projectedSpent)}</span>
        <span className="text-gray-400">/</span>
        <span className="font-medium">{formatCurrency(budget.amount)}</span>
      </div>
      {/* Mini barre de progression */}
      <div className="h-1 bg-gray-200 rounded-full overflow-hidden mt-1">
        <div
          className={clsx(
            'h-full rounded-full transition-all duration-300',
            projectedStatus === 'on_track' && 'bg-green-500',
            projectedStatus === 'warning' && 'bg-amber-500',
            projectedStatus === 'exceeded' && 'bg-red-500'
          )}
          style={{ width: `${Math.min(projectedPercent, 100)}%` }}
        />
      </div>
      {projectedRemaining < 0 && (
        <div className="text-red-600 font-medium mt-0.5">
          Dépassement de {formatCurrency(Math.abs(projectedRemaining))}
        </div>
      )}
      {/* Petite flèche vers le bas */}
      <div
        className={clsx(
          'absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-3 h-3 rotate-45 border-r border-b',
          getStatusBgColor(projectedStatus)
        )}
      />
    </div>
  );
}
